/**
 * Normalize / validate PFM node report markdown (UI + chat ingest).
 * Mirrors EAD-agent projects/pfm_node_report_content.py (keep in sync).
 */

import { nodeReportLanguageMatchers } from "./node-report-language.ts";
import {
  isIncompleteExplorationNodeReportMarkdown,
  PFM_NODE_REPORT_EXPLORATION_NEEDED_MSG,
} from "./node-report-standards.ts";

export { PFM_NODE_REPORT_EXPLORATION_NEEDED_MSG };

const NODE_REPORT_REPLY_STRIP_RE = /\[Node-Report-Reply-To:\s*[^\]]+\]\s*/gi;
export const NODE_REPORT_REPLY_RE = /\[Node-Report-Reply-To:\s*(REQ-[A-Z0-9-]+)\s*\]/i;
export const NODE_REPORT_REQUEST_RE = /\[Node-Report-Request-Id:\s*(REQ-[A-Z0-9-]+)\s*\]/i;

export type NodeReportTier = "full" | "incomplete" | "summary" | "invalid";

export function stripNodeReportReplyMarker(value: string): string {
  return String(value || "").replace(NODE_REPORT_REPLY_STRIP_RE, "").trim();
}

export function findMatchingNodeReportReply(
  messages: Array<{ role?: string; content?: string }>,
  opts: { pendingReqId?: string; nodeKey?: string },
): { content: string; reqId: string } | null {
  const pendingReq = String(opts.pendingReqId || "").trim().toUpperCase();
  const nodeKey = String(opts.nodeKey || "").trim().toLowerCase();

  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if ((msg.role || "") !== "assistant") continue;
    const content = String(msg.content || "").trim();
    if (!content) continue;
    const marker = content.match(NODE_REPORT_REPLY_RE);
    if (!marker) continue;
    const replyReqId = marker[1].toUpperCase();

    if (pendingReq && replyReqId === pendingReq) {
      return { content, reqId: marker[1] };
    }

    if (!nodeKey) continue;

    for (let j = i - 1; j >= 0; j -= 1) {
      const userMsg = messages[j];
      if ((userMsg.role || "") !== "user") continue;
      const userText = String(userMsg.content || "");
      const reqMarker = userText.match(NODE_REPORT_REQUEST_RE);
      if (!reqMarker || reqMarker[1].toUpperCase() !== replyReqId) continue;
      const reqKey = extractNodeKeyFromReportRequest(userText).toLowerCase();
      if (reqKey && reqKey === nodeKey) {
        return { content, reqId: marker[1] };
      }
      break;
    }
  }
  return null;
}

export function normalizeNodeReportMarkdown(raw: string): string {
  let text = stripNodeReportReplyMarker(raw).replace(/\r\n/g, "\n").trim();
  if (!text) return "";

  const m = nodeReportLanguageMatchers();
  const nodeHeading = text.match(m.nodeHeader);
  if (nodeHeading && nodeHeading.index != null && nodeHeading.index > 0) {
    text = text.slice(nodeHeading.index).trim();
  }

  const summaryMatch = text.match(/^node\s+summary\b/im);
  if (summaryMatch && summaryMatch.index != null && summaryMatch.index > 0) {
    text = text.slice(summaryMatch.index).trim();
  }

  const lines = text.split("\n");
  for (let i = 0; i < lines.length; i += 1) {
    const trimmed = lines[i].trim();
    if (m.nodeHeader.test(trimmed) || /^node\s+summary\b/i.test(trimmed)) {
      if (i > 0) text = lines.slice(i).join("\n").trim();
      break;
    }
  }
  return text.trim();
}

function hasBulletLines(text: string): boolean {
  return text.split("\n").some((line) => line.trim().startsWith("-"));
}

export function extractNodeKeyFromReportRequest(content: string): string {
  const text = String(content || "");
  const backtick = text.match(/\*\*Node Key:\*\*\s*`([^`]+)`/i);
  if (backtick?.[1]) return backtick[1].trim();
  const plain = text.match(/node[_\s-]*key[^\n`]*`([^`]+)`/i);
  return plain?.[1]?.trim() || "";
}

export function classifyNodeReportMarkdown(markdown: string): NodeReportTier {
  const text = normalizeNodeReportMarkdown(markdown);
  if (text.length < 80) return "invalid";

  const lower = text.toLowerCase();
  const m = nodeReportLanguageMatchers();
  const hasNodeHeader =
    m.nodeHeader.test(text) ||
    /\bnode\s+summary\b/.test(lower) ||
    m.nodeDescription.test(text);

  if (!hasNodeHeader) return "invalid";

  if (isIncompleteExplorationNodeReportMarkdown(text)) return "incomplete";

  const hasFeaturesAndTests =
    m.mainFeatures.test(text) &&
    (m.testCase.test(text) || m.procedureSteps.test(text) || m.step.test(text) || m.feature.test(text));

  const hasExplore =
    /\bexplore\s+and\s+improve\b/.test(lower) &&
    (lower.includes("what we documented well:") ||
      lower.includes("gaps and open questions:") ||
      lower.includes("recommended next exploration:") ||
      lower.includes("how to improve this report next time:") ||
      hasBulletLines(text));

  const hasSubstantiveSummary =
    (m.nodeDescription.test(text) && text.length >= 120) ||
    (/\bpurpose\b/.test(lower) && /in[-\s]?scope\s+behaviors?/.test(lower) && hasBulletLines(text));

  const hasHeadingStyleBody =
    m.nodeHeader.test(text) && text.length >= 200 && m.nodeDescription.test(text);

  if (hasFeaturesAndTests || hasExplore) return "full";
  if (hasSubstantiveSummary || hasHeadingStyleBody) return "summary";
  if (/\bnode\s+summary\b/.test(lower) && text.length >= 120) return "summary";
  return "invalid";
}

export function isSaveableNodeReportMarkdown(markdown: string): boolean {
  return classifyNodeReportMarkdown(markdown) !== "invalid";
}

/** Full-quality report (features + tests, or legacy explore block). */
export function isValidNodeReportMarkdown(markdown: string): boolean {
  return classifyNodeReportMarkdown(markdown) === "full";
}

export function isIncompleteExplorationNodeReport(markdown: string): boolean {
  return classifyNodeReportMarkdown(markdown) === "incomplete";
}
